import { QUESTION_TO_ANSWER_HEADING_TITLE } from "../constants";
import type { ModuleDraft, OrderedModule } from "./utils";

export type QtaSource = {
  moduleName: string;
  rawQuestions: string;
  expertsResponse: string;
} | null;

/** Minimal shape of the `check-draft-status` payload this derivation reads. */
export type QtaDraftStatus =
  | {
      draft_data?: Record<string, ModuleDraft> | null;
      /**
       * MDRS-94: top-level, template-independent LOI questions. `check_draft_status`
       * has no `response_model`, so this isn't in the generated client type — hence
       * the dedicated structural type here.
       */
      list_of_questions?: string | null;
    }
  | null
  | undefined;

/**
 * Derive the QtA ("Responses to Specific Questions") source data for the Report
 * Builder. The section is app-owned and always auto-inserted, so its questions
 * must populate regardless of whether the active template declares a QtA module.
 *
 * Source precedence:
 *  1. the QtA module's own `summaries_from_doc_sorter` (slug-templated happy path);
 *  2. MDRS-94 — the top-level `list_of_questions` the backend derives from the
 *     case's LOI files, template-independent (mirrors `documents_summary`);
 *  3. a content scan of `draft_data` for a numbered-list shape, covering the
 *     auto-mapper's nameless rows / legacy drafts predating (2).
 *
 * Returns `null` when the case has no LOI questions from any source.
 */
export function deriveQtaSource(
  orderedModules: OrderedModule[],
  draftStatus: QtaDraftStatus
): QtaSource {
  const qta = orderedModules.find(({ module }) =>
    QUESTION_TO_ANSWER_HEADING_TITLE.includes(module.name)
  );
  const draftData = (draftStatus?.draft_data ?? {}) as Record<string, ModuleDraft>;

  // Happy path: the template declares a QtA module and its row carries the
  // summary keyed by the module name.
  let draft: ModuleDraft | undefined = qta ? draftData[qta.module.name] : undefined;
  let rawQuestions = (draft?.summaries_from_doc_sorter ?? "").trim();

  // MDRS-94: templates without a QtA module still get the LOI questions — the
  // backend returns them as a top-level `list_of_questions`. Authoritative
  // template-independent source; prefer it over the content scan below.
  if (!rawQuestions) {
    rawQuestions = (draftStatus?.list_of_questions ?? "").trim();
  }

  // Fallback content scan — NOT gated on `qta` being present. Covers the
  // auto-mapper's nameless response_module rows (which collapse onto a single
  // `null` key and miss the direct name lookup) and legacy drafts predating
  // the aggregate write. The numbered-list shape (`1.`/`1.0`/`1)`) uniquely
  // identifies the LOI questions; other doc-sorter summaries are free-text
  // prose, so no false positives.
  if (!rawQuestions) {
    for (const entry of Object.values(draftData)) {
      const text = (entry?.summaries_from_doc_sorter ?? "").trim();
      if (!text) continue;
      if (!/^\s*\d+\s*[.)]/.test(text)) continue;
      draft = entry;
      rawQuestions = text;
      break;
    }
  }

  if (!rawQuestions) return null;
  return {
    moduleName: qta?.module.name ?? QUESTION_TO_ANSWER_HEADING_TITLE[0],
    rawQuestions,
    expertsResponse: draft?.experts_response ?? "",
  };
}
